// FinanceDashboard — replaces the legacy FinanceOverview block.
//
// Five KPI tiles + a "Where the money goes" stacked bar (with Paid 30d
// counterweight) + two side-by-side row lists (Needs attention,
// Renewals next 30 days). All math reuses the existing `getOutstanding`
// from invoiceCalc.js — the SOT for invoice math across PO Tracker and
// Invoice Tracker.

import { useEffect, useMemo, useState } from 'react';
import { aws } from '../lib/awsdb';
import { getOutstanding } from '../lib/invoiceCalc';

const SANS = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

const SEG_COLORS = {
  poCommit:   '#036E43', // jade — open POs
  outstanding:'#9A4820', // copper — invoices outstanding
  subs:       '#2A4A6E', // navy — subs monthly
};

function fmtAUD(n) { return `A$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }
function fmtShortDate(d) { return d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '—'; }

export default function FinanceDashboard({ session, onNav }) {
  const [subs, setSubs] = useState([]);
  const [fxRates, setFxRates] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [pos, setPos] = useState([]);

  useEffect(() => {
    let alive = true;
    async function load() {
      const [s, fx, inv, pay, po] = await Promise.all([
        aws.from('subscriptions').select('*').eq('status', 'active'),
        aws.from('fx_rates').select('*'),
        aws.from('po_invoices').select('id, invoice_number, supplier_name, total, amount_approved, status, due_date, currency, po_id, dispute_reason').is('archived_at', null),
        aws.from('po_invoice_payments').select('id, invoice_id, amount, paid_at'),
        aws.from('purchase_orders').select('id, total_amount, status, currency, supplier').is('archived_at', null),
      ]);
      if (!alive) return;
      setSubs(s.data || []);
      const map = {}; (fx.data || []).forEach(r => { map[r.currency_code] = Number(r.rate_to_aud) || 1; });
      setFxRates(map);
      setInvoices(inv.data || []);
      setPayments(pay.data || []);
      setPos(po.data || []);
    }
    load();
    return () => { alive = false; };
  }, []);

  // FX-aware AUD conversion. Same shape used in Subscriptions.monthlyEquiv.
  const toAUD = (amount, currency) => Number(amount || 0) * (fxRates[currency] || 1);

  // Per-invoice paid total — payments are child rows, never on the invoice.
  const paidByInvoice = useMemo(() => {
    const m = {};
    payments.forEach(p => { m[p.invoice_id] = (m[p.invoice_id] || 0) + Number(p.amount || 0); });
    return m;
  }, [payments]);

  const m = useMemo(() => {
    let outstandingAUD = 0, dueWeekAUD = 0, overdueAUD = 0, disputedAUD = 0, paid30AUD = 0;
    const today = new Date(); today.setHours(0, 0, 0, 0);

    invoices.forEach(inv => {
      const paid = paidByInvoice[inv.id] || 0;
      const out  = getOutstanding(inv, paid);
      const outA = toAUD(out, inv.currency);
      if (inv.status !== 'paid') outstandingAUD += outA;
      if (inv.status === 'disputed') disputedAUD += outA;
      if (inv.due_date) {
        const due = new Date(inv.due_date);
        const days = Math.round((due - today) / 86400000);
        if (inv.status !== 'paid' && days < 0) overdueAUD += outA;
        if (inv.status !== 'paid' && days >= 0 && days <= 7) dueWeekAUD += outA;
      }
    });

    payments.forEach(p => {
      const inv = invoices.find(i => i.id === p.invoice_id);
      if (!inv) return;
      if (!p.paid_at) return;
      const days = Math.round((today - new Date(p.paid_at)) / 86400000);
      if (days <= 30) paid30AUD += toAUD(p.amount, inv.currency);
    });

    // Open commitments — unbilled portion of POs (total - billed-against),
    // exclude received POs since their commitment closed.
    let openPoCommitAUD = 0, openPoCount = 0;
    pos.forEach(po => {
      if (po.status === 'received') return;
      openPoCount++;
      const billed = invoices.filter(i => i.po_id === po.id).reduce((s, i) => s + toAUD(i.total, i.currency), 0);
      const total = toAUD(po.total_amount, po.currency);
      openPoCommitAUD += Math.max(0, total - billed);
    });

    // Subs monthly — handles every billing cycle the existing code does.
    const monthly = (s) => {
      const aud = toAUD(s.amount, s.currency);
      if (s.billing_cycle === 'monthly') return aud;
      if (s.billing_cycle === 'quarterly') return aud / 3;
      if (s.billing_cycle === 'annually') return aud / 12;
      if (s.billing_cycle === 'custom_months' && s.custom_cycle_months > 0) return aud / s.custom_cycle_months;
      return aud;
    };
    const subsMonthlyAUD = subs.reduce((s, sub) => s + monthly(sub), 0);

    return { outstandingAUD, dueWeekAUD, overdueAUD, disputedAUD, paid30AUD, openPoCommitAUD, openPoCount, subsMonthlyAUD };
  }, [invoices, paidByInvoice, payments, pos, subs, fxRates]);

  const overdueInvoices = useMemo(() => invoices.filter(i => {
    if (i.status === 'paid' || !i.due_date) return false;
    return new Date(i.due_date) < new Date();
  }).slice(0, 6), [invoices]);

  const disputedInvoices = useMemo(() => invoices.filter(i => i.status === 'disputed'), [invoices]);

  const upcomingRenewals = useMemo(() => {
    return subs
      .filter(s => s.status === 'active' && s.next_renewal_date && new Date(s.next_renewal_date) <= new Date(Date.now() + 30 * 86400000))
      .sort((a, b) => (a.next_renewal_date || '').localeCompare(b.next_renewal_date || ''))
      .slice(0, 6);
  }, [subs]);

  const totalCommitted = m.openPoCommitAUD + m.outstandingAUD + m.subsMonthlyAUD;
  const seg = (n) => totalCommitted ? (n / totalCommitted * 100) : 0;

  const openInvCount = invoices.filter(i => i.status !== 'paid').length;
  const activeSubCount = subs.filter(s => s.status === 'active').length;

  return (
    <div style={{ padding: '24px 36px 80px', fontFamily: SANS }}>
      {/* Top alert — overdue takes priority */}
      {(overdueInvoices.length > 0 || disputedInvoices.length > 0) && (
        <TopAlert
          overdueCount={overdueInvoices.length}
          disputedCount={disputedInvoices.length}
          overdueAUD={m.overdueAUD}
          disputedAUD={m.disputedAUD}
          onReview={() => onNav('invoices')}
        />
      )}

      {/* 5 KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <KpiTile label="Outstanding"      value={fmtAUD(m.outstandingAUD)} sub={`${openInvCount} open invoices`}                          onClick={() => onNav('invoices')} />
        <KpiTile label="Due in 7 days"    value={fmtAUD(m.dueWeekAUD)}     sub="Plan payments this week"                                  onClick={() => onNav('invoices')} />
        <KpiTile label="Overdue"          value={fmtAUD(m.overdueAUD)}     sub={`${overdueInvoices.length} past due dates`} danger={m.overdueAUD > 0} onClick={() => onNav('invoices')} />
        <KpiTile label="Open commitments" value={fmtAUD(m.openPoCommitAUD)} sub={`${m.openPoCount} active POs`}                            onClick={() => onNav('potracker')} />
        <KpiTile label="Subs / month"     value={fmtAUD(m.subsMonthlyAUD)} sub={`${activeSubCount} active`}                               onClick={() => onNav('subscriptions')} />
      </div>

      {/* Where the money goes + Paid (30d) */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 220px', gap: 24,
        background: '#fff', border: '1px solid #EFEDE7', borderRadius: 12,
        padding: '18px 22px', marginBottom: 24,
      }}>
        <div>
          <div style={miniLabel}>Where the money goes</div>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', marginTop: 4 }}>
            {fmtAUD(totalCommitted)}
            <span style={{ color: '#5C6166', fontWeight: 400, fontSize: 13, marginLeft: 6 }}>
              committed across the board
            </span>
          </div>
          <div style={{
            display: 'flex', height: 8, marginTop: 12, borderRadius: 999, overflow: 'hidden',
            background: '#EFEDE7',
          }}>
            <div style={{ width: `${seg(m.openPoCommitAUD)}%`, background: SEG_COLORS.poCommit }} />
            <div style={{ width: `${seg(m.outstandingAUD)}%`, background: SEG_COLORS.outstanding }} />
            <div style={{ width: `${seg(m.subsMonthlyAUD)}%`, background: SEG_COLORS.subs }} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 22px', marginTop: 12, fontSize: 11.5, color: '#5C6166' }}>
            <LegendItem color={SEG_COLORS.poCommit}    label={`Open POs · ${fmtAUD(m.openPoCommitAUD)}`} />
            <LegendItem color={SEG_COLORS.outstanding} label={`Invoices outstanding · ${fmtAUD(m.outstandingAUD)}`} />
            <LegendItem color={SEG_COLORS.subs}        label={`Subs (monthly) · ${fmtAUD(m.subsMonthlyAUD)}`} />
          </div>
        </div>
        <div style={{ borderLeft: '1px solid #EFEDE7', paddingLeft: 24 }}>
          <div style={miniLabel}>Paid (last 30d)</div>
          <div style={{
            fontSize: 22, fontWeight: 600, marginTop: 4,
            color: '#036E43', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums',
          }}>{fmtAUD(m.paid30AUD)}</div>
          <div style={{ fontSize: 11, color: '#5C6166', marginTop: 4 }}>
            {payments.filter(p => p.paid_at && (Date.now() - new Date(p.paid_at).getTime()) / 86400000 <= 30).length} payments
          </div>
        </div>
      </div>

      {/* Two-column lists */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={sectionHead}>
            <h3 style={sectionTitle}>Needs attention</h3>
            <span style={sectionMeta}>{overdueInvoices.length + disputedInvoices.length} items</span>
          </div>
          <div style={rowListWrap}>
            {[...overdueInvoices, ...disputedInvoices].slice(0, 6).map(inv => {
              const today = new Date(); today.setHours(0, 0, 0, 0);
              const days = inv.due_date ? Math.round((today - new Date(inv.due_date)) / 86400000) : null;
              const paid = paidByInvoice[inv.id] || 0;
              const out = getOutstanding(inv, paid);
              const isDisputed = inv.status === 'disputed';
              return (
                <div key={inv.id} style={rowStyle} onClick={() => onNav('invoices')}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={rowPri}>{inv.invoice_number || '—'} · {inv.supplier_name || '—'}</div>
                    <div style={rowSec}>
                      {isDisputed ? `Disputed · ${inv.dispute_reason || 'reason on file'}` : `${days}d overdue · due ${fmtShortDate(inv.due_date)}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                    <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: '#0F1419' }}>{fmtAUD(toAUD(out, inv.currency))}</div>
                    <div style={curTag}>{inv.currency || 'AUD'} {Number(out || 0).toLocaleString()}</div>
                  </div>
                  <span style={isDisputed ? chipDisputed : chipOverdue}>{isDisputed ? 'Disputed' : 'Overdue'}</span>
                </div>
              );
            })}
            {overdueInvoices.length === 0 && disputedInvoices.length === 0 && (
              <div style={emptyStyle}>All clear</div>
            )}
          </div>
        </div>

        <div>
          <div style={sectionHead}>
            <h3 style={sectionTitle}>Renewals next 30 days</h3>
            <span style={sectionMeta}>{upcomingRenewals.length} subs</span>
          </div>
          <div style={rowListWrap}>
            {upcomingRenewals.map(s => {
              const days = Math.round((new Date(s.next_renewal_date) - Date.now()) / 86400000);
              const aud = toAUD(s.amount, s.currency);
              return (
                <div key={s.id} style={rowStyle} onClick={() => onNav('subscriptions')}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={rowPri}>
                      {s.vendor_name}
                      {s.variable_cost && (
                        <span style={{
                          marginLeft: 6, padding: '1px 7px', borderRadius: 999,
                          background: '#FCF3EC', color: '#9A4820', fontSize: 10, fontWeight: 500,
                        }}>variable</span>
                      )}
                    </div>
                    <div style={rowSec}>
                      {(s.category || '').replace(/_/g, ' ')} · {s.billing_cycle} · in {days}d
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                    <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 500, color: '#0F1419' }}>{fmtAUD(aud)}</div>
                    <div style={curTag}>{s.currency || 'AUD'} {Number(s.amount || 0).toLocaleString()}</div>
                  </div>
                </div>
              );
            })}
            {upcomingRenewals.length === 0 && (
              <div style={emptyStyle}>No renewals in the next 30 days</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TopAlert({ overdueCount, disputedCount, overdueAUD, disputedAUD, onReview }) {
  const isDanger = overdueCount > 0;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '12px 18px', borderRadius: 10, marginBottom: 18,
      background: isDanger ? '#FBEEEE' : '#FCF3EC',
      border: `1px solid ${isDanger ? '#E5C2C2' : '#E5C9B0'}`,
      color: isDanger ? '#8B2E2E' : '#9A4820',
    }}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 4.5v4M8 11v0.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>
          {overdueCount > 0 && `${overdueCount} overdue invoice${overdueCount === 1 ? '' : 's'}`}
          {overdueCount > 0 && disputedCount > 0 && ' · '}
          {disputedCount > 0 && `${disputedCount} disputed`}
        </div>
        <div style={{ fontSize: 11.5, marginTop: 2, opacity: 0.9 }}>
          {overdueCount > 0 && `${fmtAUD(overdueAUD)} past due. `}
          {disputedCount > 0 && `${fmtAUD(disputedAUD)} held while reasons resolve.`}
        </div>
      </div>
      <button onClick={onReview} style={{
        padding: '6px 12px', borderRadius: 7,
        background: 'transparent', border: '1px solid currentColor',
        color: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
      }}>Review →</button>
    </div>
  );
}

function KpiTile({ label, value, sub, danger, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', border: '1px solid #EFEDE7', borderRadius: 10,
        padding: '14px 16px', cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 120ms',
      }}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.borderColor = '#d4d2cb')}
      onMouseLeave={(e) => onClick && (e.currentTarget.style.borderColor = '#EFEDE7')}
    >
      <div style={miniLabel}>{label}</div>
      <div style={{
        fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', marginTop: 6,
        color: danger ? '#8B2E2E' : '#0F1419',
        fontVariantNumeric: 'tabular-nums',
      }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 11, marginTop: 4, color: danger ? '#8B2E2E' : '#5C6166' }}>{sub}</div>
      )}
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: color, display: 'inline-block' }} />
      {label}
    </span>
  );
}

const miniLabel = {
  fontFamily: MONO, fontSize: 9, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: '#8B9094', fontWeight: 500,
};
const sectionHead = {
  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10,
};
const sectionTitle = {
  margin: 0, fontSize: 14, fontWeight: 600, color: '#0F1419', letterSpacing: '-0.01em',
};
const sectionMeta = {
  fontFamily: MONO, fontSize: 10, color: '#8B9094', letterSpacing: '0.06em', textTransform: 'uppercase',
};
const rowListWrap = {
  display: 'flex', flexDirection: 'column', gap: 4,
  background: '#fff', border: '1px solid #EFEDE7', borderRadius: 10, overflow: 'hidden',
};
const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 14px', borderBottom: '1px solid #F4F2EC',
  cursor: 'pointer', transition: 'background 80ms',
};
const rowPri = {
  fontSize: 13, fontWeight: 500, color: '#0F1419',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const rowSec = {
  fontSize: 11.5, color: '#5C6166', marginTop: 2,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};
const curTag = {
  fontFamily: MONO, fontSize: 10, color: '#8B9094', marginTop: 2,
  letterSpacing: '0.04em',
};
const chipOverdue = {
  padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 500,
  background: '#FBEEEE', color: '#8B2E2E', flex: '0 0 auto',
};
const chipDisputed = {
  padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 500,
  background: '#FCF3EC', color: '#9A4820', flex: '0 0 auto',
};
const emptyStyle = {
  padding: '24px 16px', textAlign: 'center', color: '#8B9094', fontSize: 13,
};
